/**
 * The Rundown rail's width and its hide/show switch (#575), as rules a test can read without rendering anything.
 *
 * From lg (1024px) the rail is a column beside the content: 236px as it always was, and 300px from 1440px (90rem),
 * where a long tournament name otherwise wraps to four lines. Hidden, it is a slim handle — never gone — and the content
 * takes the width it gave up. Below lg the rail is not beside the content at all (the phone line and the tablet strip
 * are), so there it opens as an overlay instead, and always starts closed.
 *
 * The classes are whole strings on purpose: Tailwind finds a class only if it is written out somewhere it scans.
 */
export function railWidthClass(collapsed: boolean): string {
  return collapsed ? 'lg:w-8' : 'lg:w-[236px] min-[90rem]:w-[300px]';
}

export interface RailState {
  /** From lg: hidden to its handle. The remembered choice. */
  collapsed: boolean;
  /** Below lg: open as an overlay over the content. Never remembered. */
  overlayOpen: boolean;
}

/** The overlay starts closed whatever was remembered: the remembered choice is the desktop column's. */
export function initialRail(storedHidden: boolean): RailState {
  return { collapsed: storedHidden, overlayOpen: false };
}

/**
 * The one switch, wherever it is pressed. From lg it hides the column to its handle or shows it again; below lg it opens
 * or closes the overlay and leaves the remembered choice alone.
 */
export function toggleRail(state: RailState, large: boolean): RailState {
  return large
    ? { collapsed: !state.collapsed, overlayOpen: false }
    : { collapsed: state.collapsed, overlayOpen: !state.overlayOpen };
}

/** An overlay left open while the window widens past lg would sit over the column it became; close it. */
export function atBreakpoint(state: RailState, large: boolean): RailState {
  return large && state.overlayOpen ? { ...state, overlayOpen: false } : state;
}

/** What pressing the switch will do. */
export function switchLabel(expanded: boolean): string {
  return expanded ? 'Hide rundown' : 'Show rundown';
}

/**
 * The rail's `<aside>`. Below lg it is not drawn until opened, and then floats over the content (`absolute` inside the
 * shell's content row) rather than pushing it; from lg it is the column beside the content, as wide as it is set.
 */
export function railClass(state: RailState): string {
  const small = state.overlayOpen
    ? 'absolute inset-y-0 left-0 z-30 flex w-[min(300px,85vw)] shadow-2xl'
    : 'hidden';
  return `shrink-0 flex-col bg-rail-900 ${small} lg:static lg:z-auto lg:flex lg:self-stretch lg:shadow-none ${railWidthClass(state.collapsed)}`;
}

/** The running order inside it: hidden from lg while the column is a handle, always drawn in the overlay. */
export function railContentClass(state: RailState): string {
  return state.collapsed ? 'lg:hidden' : '';
}

/** The handle: only from lg, and only while the column is hidden. */
export function railHandleClass(state: RailState): string {
  return state.collapsed ? 'hidden lg:flex' : 'hidden';
}
